import {
  calculateDistance,
  normalizeLiquidationSettings,
} from '../logistics/components/LogisticsUtils';

const SUPPORTED_TRANSPORT_TYPES = new Set(['moto', 'camioneta', 'auto', 'camion']);

const resolveServiceTransportType = (serviceTransport = '', legacyServiceType = '') => {
  const normalized = String(serviceTransport || legacyServiceType || '').trim().toLowerCase();
  if (SUPPORTED_TRANSPORT_TYPES.has(normalized)) return normalized;
  return 'camioneta';
};

const resolveCommercialConfig = (service = {}) => ({
  commercialModel: String(service?.commercialModel || '').trim().toLowerCase(),
  pricingRule: String(service?.pricingRule || '').trim().toLowerCase(),
  manualTotal:
    Number(
      service?.salePrice ??
      service?.agreedPricePerTripSnapshot ??
      service?.agreedPricePerTrip ??
      0
    ) || 0,
  fixedMarginAmount:
    Number(
      service?.fixedMarginAmountSnapshot ??
      service?.fixedMarginAmount ??
      0
    ) || 0,
  marginPercent:
    Number(
      service?.marginPercentSnapshot ??
      service?.marginPercent ??
      0
    ) || 0,
  monthlyFee:
    Number(
      service?.monthlyFeeSnapshot ??
      service?.monthlyFee ??
      0
    ) || 0,
});

const DEFAULT_MARGIN_PERCENT = 40;

const findScalePrice = (distanceKm, costScales = []) => {
  const matchedScale = (Array.isArray(costScales) ? costScales : []).find((scale) => {
    const from = Number(scale?.from ?? 0) || 0;
    const to =
      scale?.to === '' || scale?.to === null || scale?.to === undefined
        ? null
        : Number(scale.to) || 0;

    if (to === null) return distanceKm >= from;
    return distanceKm >= from && distanceKm < to;
  });

  return matchedScale ? Number(matchedScale?.price ?? 0) || 0 : 0;
};

const computeLegCost = (distanceKm, config = {}) => {
  if (config.costModel === 'byScale') {
    return findScalePrice(distanceKm, config.costScales);
  }

  return distanceKm * (Number(config.pricePerKm) || 0);
};

export const estimateServiceCommercialMetrics = ({
  service = {},
  liquidationSettings = {},
} = {}) => {
  const originSnapshot = service?.originSnapshot || null;
  const destinationSnapshot = service?.destinationSnapshot || null;
  const additionalStops = Array.isArray(service?.additionalStops) ? service.additionalStops : [];

  const orderedStops = [destinationSnapshot, ...additionalStops].filter(
    (stop) => stop?.latlng && Number.isFinite(Number(stop.latlng.lat)) && Number.isFinite(Number(stop.latlng.lng))
  );

  if (
    !originSnapshot?.latlng ||
    !Number.isFinite(Number(originSnapshot.latlng.lat)) ||
    !Number.isFinite(Number(originSnapshot.latlng.lng)) ||
    orderedStops.length === 0
  ) {
    return {
      transportType: resolveServiceTransportType(service?.serviceTransport, service?.serviceType),
      estimatedDistanceKm: 0,
      estimatedOperationalCost: 0,
      estimatedMargin: 0,
      estimatedTotalToCharge: 0,
      monthlyFeeSnapshot: Number(service?.monthlyFeeSnapshot || 0) || 0,
      hasEstimate: false,
    };
  }

  const normalizedLiquidation = normalizeLiquidationSettings(liquidationSettings || {});
  const transportType = resolveServiceTransportType(service?.serviceTransport, service?.serviceType);
  const config = normalizedLiquidation?.transports?.[transportType] || {
    costModel: normalizedLiquidation?.defaultCostModel || 'perKm',
    pricePerKm: Number(normalizedLiquidation?.defaultPricePerKm || 0) || 0,
    baseRouteFee: 0,
    minimumRouteFee: 0,
    costScales: [],
  };

  let totalDistanceKm = 0;
  let totalOperationalCost = 0;
  let previousPoint = {
    lat: Number(originSnapshot.latlng.lat),
    lng: Number(originSnapshot.latlng.lng),
  };

  orderedStops.forEach((stop) => {
    const currentPoint = {
      lat: Number(stop.latlng.lat),
      lng: Number(stop.latlng.lng),
    };
    const legDistance = calculateDistance(
      previousPoint.lat,
      previousPoint.lng,
      currentPoint.lat,
      currentPoint.lng
    );
    totalDistanceKm += legDistance;
    totalOperationalCost += computeLegCost(legDistance, config);
    previousPoint = currentPoint;
  });

  totalOperationalCost += Number(config.baseRouteFee || 0) || 0;
  if (Number(config.minimumRouteFee || 0) > 0) {
    totalOperationalCost = Math.max(totalOperationalCost, Number(config.minimumRouteFee || 0) || 0);
  }

  const commercialConfig = resolveCommercialConfig(service);
  const usesMonthlyFixed = commercialConfig.commercialModel === 'monthly_fixed';

  let estimatedMargin = 0;
  let estimatedTotalToCharge = 0;

  if (usesMonthlyFixed) {
    estimatedMargin = 0;
    estimatedTotalToCharge = 0;
  } else if (commercialConfig.pricingRule === 'fixed_margin') {
    estimatedMargin = commercialConfig.fixedMarginAmount;
    estimatedTotalToCharge = totalOperationalCost + estimatedMargin;
  } else if (commercialConfig.pricingRule === 'percentage_margin') {
    const appliedPercent = commercialConfig.marginPercent > 0 ? commercialConfig.marginPercent : DEFAULT_MARGIN_PERCENT;
    estimatedMargin = totalOperationalCost * (appliedPercent / 100);
    estimatedTotalToCharge = totalOperationalCost + estimatedMargin;
  } else {
    if (commercialConfig.manualTotal > 0) {
      estimatedTotalToCharge = commercialConfig.manualTotal;
      estimatedMargin = estimatedTotalToCharge - totalOperationalCost;
    } else {
      estimatedMargin = totalOperationalCost * (DEFAULT_MARGIN_PERCENT / 100);
      estimatedTotalToCharge = totalOperationalCost + estimatedMargin;
    }
  }

  return {
    transportType,
    estimatedDistanceKm: totalDistanceKm,
    estimatedOperationalCost: totalOperationalCost,
    estimatedMargin,
    estimatedTotalToCharge: estimatedTotalToCharge > 0 ? estimatedTotalToCharge : 0,
    monthlyFeeSnapshot: commercialConfig.monthlyFee,
    hasEstimate: totalDistanceKm > 0 || totalOperationalCost > 0,
  };
};
